/**
 * Diagnostic logging singleton — batches events and POSTs to /api/diagnostics/log.
 * Fire-and-forget: never throws, never awaited by callers.
 */

import { CLOUD_API_URL } from '../config/serverConfig';

const LS_SESSION_ID = 'maestro_diagnostic_session_id';
const LS_ACTIVE = 'maestro_diagnostic_active';

/** Coalesce uncaught / React errors so render loops cannot flood JSONL. */
const COALESCE_MS = 10_000;
const COALESCE_CAP_WINDOW_MS = 10 * 60_000;
const COALESCE_MAX_LINES_PER_SIG = 12;

export type DiagnosticLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error';
export type DiagnosticCategory =
  | 'discovery'
  | 'transport'
  | 'volume'
  | 'playback'
  | 'protocol'
  | 'capability'
  | 'navigation'
  | 'uncaught'
  | 'react'
  | 'react-185'
  | 'console-error'
  | 'console-warn'
  | 'js:billing'
  | 'js:playback'
  | 'js:radio'
  | 'js:render'
  | 'billing'
  | 'updater'
  | 'signal-path'
  | 'other';

export interface DiagnosticEventInput {
  level: DiagnosticLevel;
  category: DiagnosticCategory;
  message: string;
  payload?: Record<string, unknown>;
  device_fingerprint?: string;
}

const COALESCED_CATEGORIES = new Set<DiagnosticCategory>([
  'uncaught',
  'react',
  'react-185',
  'console-error',
  'console-warn',
  'playback',
  'navigation',
]);

interface CoalesceState {
  windowStart: number;
  totalOccurrences: number;
  linesEmitted: number;
  lastEmitAt: number;
}

function getAuthTokenSync(): string | null {
  try {
    const stored = localStorage.getItem('maestro_auth');
    if (stored) return JSON.parse(stored).accessToken || null;
  } catch {
    /* ignore */
  }
  return null;
}

function normalizeCoalesceSignature(message: string): string {
  return message
    .replace(/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/g, '{ip}')
    .replace(/\d{4,}/g, '{n}')
    .replace(/\s+\(coalesced \d+ occurrences\)$/i, '')
    .slice(0, 200);
}

const SUPPORT_SNAPSHOT_MAX = 600;

class DiagnosticLogger {
  private buffer: DiagnosticEventInput[] = [];
  /** Always-on ring for support ticket .txt attachments (independent of diagnostic session). */
  private supportSnapshot: Array<DiagnosticEventInput & { ts: number }> = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private _hasTauri: boolean | null = null;
  private _inEmit = false;
  private coalesceMap = new Map<string, CoalesceState>();
  /** Drop back-to-back identical lines (queue-state loops, etc.). */
  private lastEmitSignature: string | null = null;

  private get hasTauri(): boolean {
    if (this._hasTauri === null) {
      this._hasTauri =
        typeof window !== 'undefined' && '__TAURI__' in window;
    }
    return this._hasTauri;
  }

  get isActive(): boolean {
    try {
      return (
        localStorage.getItem(LS_ACTIVE) === 'true' &&
        !!localStorage.getItem(LS_SESSION_ID)
      );
    } catch {
      return false;
    }
  }

  /** Object-style API */
  log(event: DiagnosticEventInput): void {
    try {
      if (COALESCED_CATEGORIES.has(event.category)) {
        this.logCoalesced(event);
        return;
      }
      this.emitLog(event);
    } catch {
      /* never propagate */
    }
  }

  private logCoalesced(event: DiagnosticEventInput): void {
    const sig = `${event.category}::${normalizeCoalesceSignature(event.message)}`;
    const now = Date.now();
    let state = this.coalesceMap.get(sig);
    if (!state || now - state.windowStart > COALESCE_CAP_WINDOW_MS) {
      state = { windowStart: now, totalOccurrences: 0, linesEmitted: 0, lastEmitAt: 0 };
      this.coalesceMap.set(sig, state);
    }
    state.totalOccurrences += 1;

    if (state.linesEmitted === 0) {
      this.emitLog({
        ...event,
        payload: { ...event.payload, occurrence_count: 1 },
      });
      state.linesEmitted = 1;
      state.lastEmitAt = now;
      return;
    }

    if (state.linesEmitted >= COALESCE_MAX_LINES_PER_SIG) return;

    if (now - state.lastEmitAt >= COALESCE_MS) {
      this.emitLog({
        ...event,
        message: `${event.message} (coalesced ${state.totalOccurrences} occurrences)`,
        payload: {
          ...event.payload,
          occurrence_count: state.totalOccurrences,
          coalesced: true,
        },
      });
      state.linesEmitted += 1;
      state.lastEmitAt = now;
    }
  }

  private pushSupportSnapshot(event: DiagnosticEventInput): void {
    try {
      this.supportSnapshot.push({ ...event, ts: Date.now() });
      if (this.supportSnapshot.length > SUPPORT_SNAPSHOT_MAX) {
        this.supportSnapshot.splice(0, this.supportSnapshot.length - SUPPORT_SNAPSHOT_MAX);
      }
    } catch {
      /* ignore */
    }
  }

  /** Recent structured diagnostic events for support log attachments. */
  getSupportSnapshot(): Array<DiagnosticEventInput & { ts: number }> {
    return [...this.supportSnapshot];
  }

  private emitLog(event: DiagnosticEventInput): void {
    if (this._inEmit) return;
    const signature = `${event.category}::${event.message}::${JSON.stringify(event.payload ?? null)}`;
    if (signature === this.lastEmitSignature) {
      return;
    }
    this._inEmit = true;
    try {
      this.lastEmitSignature = signature;
      this.pushSupportSnapshot(event);
      if (this.hasTauri) {
        try {
          (window as any).__TAURI__.core
            .invoke('diagnostic_log_js', {
              level: event.level,
              category: event.category,
              message: event.message,
              data: event.payload || null,
            })
            .catch(() => {});
        } catch {
          /* silent */
        }
      }

      if (!this.isActive) return;
      this.buffer.push(event);
      if (!this.flushTimer) {
        this.flushTimer = setInterval(() => void this.flush(), 3000);
      }
    } catch {
      /* never propagate */
    } finally {
      this._inEmit = false;
    }
  }

  /** Flush buffered events (single POST per event — API accepts one event per request). */
  private async flush(): Promise<void> {
    if (this.buffer.length === 0) {
      if (this.flushTimer) {
        clearInterval(this.flushTimer);
        this.flushTimer = null;
      }
      return;
    }

    const events = this.buffer.splice(0, this.buffer.length);
    const sessionId = (() => {
      try {
        return localStorage.getItem(LS_SESSION_ID);
      } catch {
        return null;
      }
    })();
    if (!sessionId) {
      if (this.flushTimer) {
        clearInterval(this.flushTimer);
        this.flushTimer = null;
      }
      return;
    }

    const token = getAuthTokenSync();
    if (!token) return;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    try {
      await Promise.all(
        events.map((e) =>
          fetch(`${CLOUD_API_URL}/api/diagnostics/log`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              session_id: sessionId,
              level: e.level,
              category: e.category,
              message: e.message,
              payload: e.payload ?? null,
              device_fingerprint: e.device_fingerprint ?? undefined,
            }),
          }).catch(() => {}),
        ),
      );
    } catch {
      /* swallow */
    }

    if (this.buffer.length === 0 && this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /** Stop timer and attempt a final flush (non-blocking). */
  stop(): void {
    try {
      if (this.flushTimer) {
        clearInterval(this.flushTimer);
        this.flushTimer = null;
      }
      void this.flush();
    } catch {
      /* ignore */
    }
  }

  /** Test-only: clear coalesce/snapshot state without touching localStorage session flags. */
  resetForTests(): void {
    this.buffer = [];
    this.supportSnapshot = [];
    this.coalesceMap.clear();
    this.lastEmitSignature = null;
    this._inEmit = false;
  }
}

export const diagnosticLogger = new DiagnosticLogger();

/** Legacy functional API — (level, category, message, payload?, deviceFingerprint?) */
export function diagnosticLog(
  level: string,
  category: string,
  message: string,
  payload?: Record<string, unknown>,
  deviceFingerprint?: string,
): void {
  diagnosticLogger.log({
    level: level as DiagnosticLevel,
    category: category as DiagnosticCategory,
    message,
    payload,
    device_fingerprint: deviceFingerprint,
  });
}

export function isDiagnosticActive(): boolean {
  try {
    return (
      localStorage.getItem(LS_ACTIVE) === 'true' &&
      !!localStorage.getItem(LS_SESSION_ID)
    );
  } catch {
    return false;
  }
}

export function setDiagnosticSession(sessionId: string | null, active: boolean): void {
  try {
    if (sessionId) {
      localStorage.setItem(LS_SESSION_ID, sessionId);
    } else {
      localStorage.removeItem(LS_SESSION_ID);
    }
    localStorage.setItem(LS_ACTIVE, active ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

export function clearDiagnosticSession(): void {
  diagnosticLogger.stop();
  try {
    localStorage.removeItem(LS_SESSION_ID);
    localStorage.removeItem(LS_ACTIVE);
    localStorage.removeItem('maestro_diagnostic_ticket_number');
  } catch {
    /* ignore */
  }
}

/** Structured events captured this session for support ticket attachments. */
export function getSupportDiagnosticEvents(): Array<DiagnosticEventInput & { ts: number }> {
  return diagnosticLogger.getSupportSnapshot();
}

/** Test-only: stop flush timer and clear coalesce state between isolated imports. */
export function resetDiagnosticLoggerForTests(): void {
  diagnosticLogger.stop();
  diagnosticLogger.resetForTests();
}
